import {
    union,
    difference,
    differenceBy,
    find,
    forEach,
    filter,
    map,
    orderBy,
    capitalize,
    isEmpty,
    sortBy
} from "lodash";
import { DateTime } from "luxon";
import ACTIONS from "@/constants/actions";
import Lab from "@/services/lab";
import ITEMTYPES from "@/constants/item-types";
import EXTENSIONS from "@/constants/extensions";

const entities = require("entities");
const LAB_USE_CLINIC_PROVIDER_MSG =
    "Provider for lab/radiology item cannot be clinic default provider. Please select a different provider.";
const DUE_MUST_BE_LATER_MSG = "'Due' date has to be later than 'Given' date";
const ANALYZER_OFFLINE_MSG = "Selected analyzer is offline.";
const VETLAB_STATION_OFFLINE_MSG = "Selected VetLab Station is offline.";
const REFILL_MINIMUM_MSG =
    "Refill amount cannot be lower than remaining refills.";
const INSTRUCTION_COUNT = 117;
const RECORD_NOTE_COUNT = 255;
const PERSONAL_NOTE_COUNT = 500;
const SET_MEDICAL_NOTES_OPTIONS = true;
/**
 * @method Record Mixin
 * @descriotion all functions used for create record
 * Used by
 * - patient > New Record
 * - patient > Edit Record
 * - invoice > item > New item
 * - invoice > item > Edit Item
 * - Maybe can used by patient > create record too as well as most functions are same
 * - But still need to check each code
 */
export default {
    SET_MEDICAL_NOTES_OPTIONS,
    INSTRUCTION_COUNT,
    RECORD_NOTE_COUNT,
    PERSONAL_NOTE_COUNT,
    LAB_USE_CLINIC_PROVIDER_MSG,
    REFILL_MINIMUM_MSG,
    computed: {
        showAlert() {
            return this.alertMessages.length > 0;
        },
        routesFilter() {
            return [
                { value: "0", text: "None" },
                ...this.routes.map(route => ({
                    value: route.id,
                    text: route.value
                }))
            ];
        },
        locationsFilter() {
            return [
                { value: "0", text: "None" },
                ...this.locations.map(location => ({
                    value: location.id,
                    text: location.value
                }))
            ];
        },
        filledBy() {
            return [
                { value: "0", text: "Third Party" },
                { value: "2", text: "Online Store" },
                { value: "3", text: "Direct Shipped" },
                {
                    value: "1",
                    text: this.$root.setting.business
                        ? this.$root.setting.business.name
                        : ""
                }
            ];
        },
        lotOption() {
            let options = [];
            for (const lot of this.itemLotOption) {
                let expireDate = lot.expire || lot.inventory_expire;
                // Checking, as edit will have duplicated lot purchase from form_data and API get purchases
                if (find(options, ["value", lot.id])) {
                    continue;
                }

                if (expireDate) {
                    expireDate = this._toDateTime(expireDate);
                    expireDate = expireDate.toFormat(this._dateFormat);
                }

                options.push({
                    id: lot.id, // custom will undefined, use in data.quantity calculation
                    balance: lot.balance || lot.lot_quantity, // to calculate used lot on watch data.quantity
                    value: lot.id,
                    text:
                        lot.text ||
                        this.lotOptionText({
                            name: lot.lot,
                            manufacturer: lot.manufacturer,
                            quantity: lot.balance || lot.lot_quantity,
                            expire: expireDate,
                            unitMeasure: lot?.unit_measure
                        })
                });
            }
            return options;
        },
        diagnosisFilter() {
            return difference(this.diagnosisOption, this.data.diagnosis);
        },
        lotOptionFilter() {
            // Use differenceBy to handle object using value
            return differenceBy(this.lotOption, this.data.lot, "value");
        }
    },
    watch: {
        "data.instruction"(value) {
            let valueLength = 0;
            let count = 200;
            if (value) {
                valueLength = value.length;
            }

            if (this.searchOnlinePharmacyOnly) {
                count = INSTRUCTION_COUNT;
            }
            this.instructionCount = count - valueLength;

            if (this.instructionCount < 0) {
                this.continues = false;
            } else {
                this.continues = true;
            }
        },
        "data.personal_note"(value) {
            let valueLength = 0;
            if (value) {
                valueLength = value.length;
            }
            this.personalNoteCount = PERSONAL_NOTE_COUNT - valueLength;

            if (this.personalNoteCount < 0 && this.searchOnlinePharmacyOnly) {
                this.personalNoteContinues = false;
            } else {
                this.personalNoteContinues = true;
            }
        },
        "data.record_note"(value) {
            //validate to have only 255 characters
            let valueLength = 0;
            if (value) {
                valueLength = value.length;
            }
            this.recordNoteCount = 255 - valueLength;

            if (this.recordNoteCount < 0 && this.searchOnlinePharmacyOnly) {
                this.recordNoteContinues = false;
            } else {
                this.recordNoteContinues = true;
            }
        },
        "data.given_date"(value, oldValue) {
            let calculateNextDue = true;
            // Fix for issue VET-3389
            // To prevent next due from being replaces before the data is completely load
            if (this.disableWatcher) {
                // currently use in editRecord and refillRecord function only
                this.disableWatcher = false;
                calculateNextDue = false;
            }
            // Check if newValue and oldValue are both valid moment objects
            // Might due to view/src/module/commons/date-time-picker.vue triggering an update for 2nd time.
            if (moment.isMoment(value) && moment.isMoment(oldValue)) {
                // Compare the two moments.
                // Compare just the dates, not the time.
                if (value.isSame(oldValue, "day")) {
                    // If they are the same, skip the following code
                    calculateNextDue = false;
                }
            }

            if (calculateNextDue && value && this.itemNextDue) {
                if (
                    this.params &&
                    this.params.module === this.MODULE.TREATMENT_BOARD
                ) {
                    this.data.next_due = value
                        .clone()
                        .add(this.itemNextDue, "seconds")
                        .format(this.datePickerOptions.format);
                } else {
                    this.data.next_due = value
                        .clone()
                        .add(this.itemNextDue, this.itemNextDuePeriod)
                        .format(this.getDateFormat());
                }
            }
            this.checkDueDate();
        },
        "data.next_due"(value) {
            this.checkDueDate();
        },
        "data.refill"(value) {
            if (value == 0 && this.errors.has("expire")) {
                this.errors.remove("expire");
            }
        },
        "data.expire"(value) {
            if (this.data.refill > 0 && this.errors.has("expire") && value) {
                this.errors.remove("expire");
            }
        }
    },
    methods: {
        /**
         * fetchOnly = fetch pre-defined lots and append to Lot Number options.
         * By default, `fetchOnly` is set to false. It means if there are any predefined options available
         * for the item, Lot Number will be set to the first option automatically.
         * To prevent this (auto assign), set `fetchOnly` to `true`.
         */
        async getInventoryLot(itemId, fetchOnly = false) {
            let self = this;
            let givenDate = this.data.given_date
                ? this.data.given_date
                : self.$parent.$parent.$parent.startDate;
            this.loadingLot = true;
            await self.$http
                .get(
                    process.env.BARRACUDA +
                        "inventory/search-lot/" +
                        itemId +
                        "?date=" +
                        givenDate
                            .clone()
                            .set({ hour: 0, minute: 0, second: 0 })
                            .unix()
                )
                .then(function (response) {
                    if (response.data.response.purchase) {
                        if (
                            self.data.item_type == 7 &&
                            (self.$parent.$parent.$parent.isNew ||
                                self.$parent.$parent.$parent.isDuplicate)
                        ) {
                            self.itemLotOption = [];
                            self.data.lot = [];
                        }
                        // USE MERGE AS EDIT WILL HAVE DATA FROM form_data
                        self.itemLotOption = [
                            ...self.itemLotOption,
                            ...response.data.response.purchase
                        ];
                        if (self.lotOption.length > 0) {
                            if (!fetchOnly) {
                                let countOfPatient = 1;
                                if (self.data.isMultiplePatient) {
                                    countOfPatient = self.data.patients.length;
                                }
                                let unused =
                                    self.data.quantity * countOfPatient;
                                for (let lot of self.lotOption) {
                                    if (
                                        unused > 0 &&
                                        !find(self.data.lot, ["value", lot.id])
                                    ) {
                                        self.data.lot.push(lot);
                                        unused = unused - lot.balance;
                                    }
                                }
                            }
                        }
                    }
                })
                .finally(() => {
                    this.loadingLot = false;
                });
        },
        checkDueDate() {
            this.isDueError = false;
            if (this.data.next_due && this.data.given_date) {
                let due = DateTime.fromFormat(
                    this.data.next_due,
                    this._dateFormat
                );

                if (due <= this.data.given_date) {
                    this.isDueError = true;
                    this.pushAlertMessages(DUE_MUST_BE_LATER_MSG);
                } else if (this.alertMessages.includes(DUE_MUST_BE_LATER_MSG)) {
                    this.removeAlertMessages(DUE_MUST_BE_LATER_MSG);
                }
            }
        },
        showDatePicker(event) {
            // fix position of the date picker
            this._datePickerShow(event);
        },
        /**
         * @method applyLot
         * @param {Object} recordResources
         * The recordResources is from api services>records.js>editRecordDetails()
         * ${baseURLci}patient/record/edit
         */
        applyLot(recordResources, updateLot = false) {
            // somehow, this function only applicable for edit record only
            // For medical record patient (record/create-update): duplicate and refill will have same flow as create for lot purchase
            let customs = recordResources.form_data.custom_lots || [];
            this.data.lot = [];

            // purchase lots
            let selectedPurchaseIds =
                recordResources.form_data.purchase_ids || [];
            let unsortedPurchase = recordResources.form_data.purchase || [];
            let purchase = sortBy(unsortedPurchase, item => {
                const index = selectedPurchaseIds.indexOf(item.id);
                return index === -1 ? Infinity : index; // Place unsorted items at the end
            });
            let unused = this.data.quantity;

            // Process purchases
            purchase.forEach(value => {
                const generatedPurchase = {
                    id: value.id,
                    balance: value.balance,
                    value: value.id,
                    text: value.label
                };

                const isSelected = selectedPurchaseIds.includes(value.id);

                if (updateLot) {
                    // only use by invoice
                    if (isSelected && unused > 0) {
                        // Add previously assigned lots
                        this.data.lot.push(generatedPurchase);
                        unused -= generatedPurchase.balance;
                        this.itemLotOption.push(generatedPurchase);
                    } else if (!isSelected) {
                        // Add other available lots if there's unused quantity
                        if (unused > 0) {
                            this.data.lot.push(generatedPurchase);
                            unused -= generatedPurchase.balance;
                        }
                        this.itemLotOption.push(generatedPurchase);
                    }
                } else {
                    // For create or duplicate actions
                    this.itemLotOption.push(generatedPurchase);
                    if (isSelected) {
                        this.data.lot.push(generatedPurchase);
                    }
                }
            });
            // custom lots
            for (const rec of customs) {
                let expireDate = rec.inventory_expire;
                if (expireDate) {
                    expireDate = DateTime.fromFormat(
                        expireDate,
                        this._dateFormat
                    );
                    expireDate = expireDate.toFormat(this._dateFormat);
                }

                this.data.lot.push({
                    value: JSON.stringify({
                        ...rec,
                        inventory_expire: expireDate
                    }),
                    text: this.lotOptionText({
                        name: rec.lot,
                        manufacturer: rec.manufacturer,
                        quantity: rec.balance || rec.lot_quantity,
                        expire: expireDate
                    })
                });
            }
        },
        onChangeLot(value) {
            let self = this;
            self.data.lot.push({ text: value.text, value: value.id });
        },
        addLot() {
            this.$refs.newNewLotWindow.open(this.data.uom);
        },
        getRoutes() {
            let self = this;
            return this.$http
                .get(
                    process.env.BARRACUDA + "reference/master-vaccination-route"
                )
                .then(function (response) {
                    self.routes = response.data.response.resources;
                });
        },
        getLocation() {
            let self = this;
            return this.$http
                .get(
                    process.env.BARRACUDA +
                        "reference/master-vaccination-location"
                )
                .then(function (response) {
                    self.locations = response.data.response.resources;
                });
        },
        getDiagnosis(patientID) {
            let self = this;
            return this.$http
                .get(
                    process.env.BARRACUDA +
                        "record/diagnosis/" +
                        patientID +
                        "?status=opened&all=true"
                )
                .then(function (response) {
                    if (response.data.response.resources) {
                        self.diagnosisOption = response.data.response.resources.map(
                            value => ({
                                value: value.id,
                                text: value.description
                            })
                        );
                        self.appendEditDiagnoses();
                    }
                    return self.diagnosisOption;
                });
        },
        getPrinterList() {
            let self = this;
            if (this._isExtensionEnabled("VETTER_PRINTER")) {
                return this.$http
                    .get(process.env.BARRACUDA + "account/printer")
                    .then(function (response) {
                        if (response.data.response.resources) {
                            self.printerList = union(
                                [{ value: "no", text: "Do not print label" }],
                                response.data.response.resources.map(
                                    printer => ({
                                        value: printer.id,
                                        text: printer.name
                                    })
                                )
                            );
                            response.data.response.resources.map(printer => {
                                if (printer.is_default == 1) {
                                    self.defaultPrinter = printer.id;
                                }
                            });
                        }
                    });
            } else {
                self.printerList = union(
                    [{ value: "no", text: "Do not print label" }],
                    [{ value: "local", text: "Generate PDF" }]
                );
            }
        },
        async printLabel(dataLabel) {
            let self = this;
            let module = "prescription";
            switch (dataLabel.type) {
                case 4:
                    module = "vaccination";
                    break;
                case 6:
                    module = "lab";
                    break;
                case 7:
                    module = "service";
                    break;
            }
            let result = await this.$http.put(
                process.env.APPS +
                    "index.php/patient/" +
                    module +
                    "/print_label_new?record_id=" +
                    dataLabel.id +
                    "&module_type=" +
                    dataLabel.type +
                    "&patient_id=" +
                    dataLabel.patient_id +
                    "&printer=" +
                    dataLabel.printer_id +
                    "&print_quantity=" +
                    dataLabel.print_quantity
            );

            if (result.data.state == 0) {
                if (result.data.pdf) {
                    window.open(result.data.pdf);
                } else {
                    self._message(result.data.message, "success");
                }
            } else {
                self._message(result.data.message, "danger");
            }
        },
        lotOptionText({
            name,
            manufacturer,
            quantity,
            expire,
            unitMeasure = "Unit(s)"
        }) {
            if (isEmpty(unitMeasure)) {
                unitMeasure = "Unit(s)";
            }

            return `${name ? name + ";" : ""} ${manufacturer ? "(" + manufacturer + ");" : ""} ${quantity} ${unitMeasure} remaining${expire ? "; Expires " + expire : ""}`; // prettier-ignore
        },
        setPrinterLabel(printLabel) {
            if (printLabel) {
                const printersExcludeNo = filter(
                    this.printerList,
                    printer => printer.value != "no"
                );
                this.data.printer =
                    printLabel == "0" || printersExcludeNo.length == 0
                        ? "no"
                        : this.defaultPrinter || printersExcludeNo[0].value;
            } else {
                /* if printLabel undefined/null, set to Do not print label*/
                this.data.printer = "no";
            }
        },
        addActions(actions, needExtract) {
            let self = this;

            if (needExtract) {
                forEach(actions, function (value) {
                    if (value.action.id !== ACTIONS.ID.CREATE_ALERT) {
                        var text = value.action.label ?? "";

                        if (value.task_content) {
                            text += "(" + value.task_content + ")";
                        }

                        if (
                            _.isEmpty(text) &&
                            value.action &&
                            value.action.type
                        ) {
                            switch (value.action.id) {
                                case ACTIONS.ID.PRINT_CERTIFICATE:
                                    text = "Print";
                                    if (value.value) {
                                        text += " " + value.value;
                                    }

                                    text = capitalize(text);
                                    break;
                                case ACTIONS.ID.CREATE_REMINDER:
                                    text = "Create reminder(s)";
                                    break;
                                case ACTIONS.ID.CREATE_TASK:
                                    text = value.action.type;
                                    if (value.value) {
                                        text += " (" + value.value + ")";
                                    }
                                    break;
                                default:
                                    text = value.action.type;
                            }
                        }
                        self.data.action.push({
                            value: value.action.id + "#$" + value.id,
                            text
                        });
                    }
                });
            } else {
                forEach(actions, function (value, key) {
                    self.data.action.push({
                        value: key,
                        text: value
                    });
                });
            }

            self.actions = self.data.action;
        },
        decodedHe: function (string) {
            return entities.decodeHTML(string);
        },
        getMedicalNote(patientID, setOptions = SET_MEDICAL_NOTES_OPTIONS) {
            // If send patient name, the patient name will append on the infront of the medical note label
            // This is used in Invoice > New Item
            let self = this;
            let medicalNote = [];
            return this.$http
                .get(
                    process.env.BARRAMUNDI +
                        "record/medical-note-list/" +
                        patientID
                )
                .then(function (response) {
                    if (response.data.response.resources) {
                        forEach(response.data.response.resources, function (
                            value
                        ) {
                            let name = "";
                            let date = "";
                            if (value.date) {
                                date = ` (${self
                                    ._toDateTime(value.date)
                                    .toFormat(self._dateFormat)})`;
                            }

                            switch (value.type) {
                                case 0:
                                case 1:
                                case 2:
                                    name = `Medical Note ${date}`;
                                    break;
                                default:
                                    if (value.template_id) {
                                        name = value.template_name + date;
                                    }
                            }
                            if (value.title) {
                                name = `${value.title} ${date}`;
                            }

                            medicalNote.push({
                                patientId: patientID,
                                value: value.id,
                                text: name,
                                date: value.date
                            });
                        });

                        // Set the option to be used in the select component
                        // If not, the parent will set the option itself
                        if (setOptions) {
                            self.medicalNotes = union(
                                [{ value: -1, text: "No Medical Note" }],
                                medicalNote
                            );
                        }
                        return medicalNote;
                    }
                });
        },
        initLabFields(details) {
            let self = this;
            self.originalSourcesLength = null;

            if (
                typeof details.sources != "undefined" &&
                details.sources != null &&
                details.sources != "null"
            ) {
                let sourceLength = Object.keys(details.sources).length;
                self.originalSourcesLength = sourceLength;

                if (sourceLength > 0) {
                    // show source field if sourceLength is more than 1,
                    // has been handled with v-if with originalSourcesLength
                    self.sourceOptions = [];
                    self.preloadSourceOptions(details.source, details.sources);
                }
            }

            // check if there is source in the sourceOptions , and set to that
            if (find(self.sourceOptions, ["value", details.source])) {
                self.data.source = details.source;
            } else {
                // else set source = OTHER n sourceOptions = [OTHER]
                self.sourceOptions = [];
                self.sourceOptions.push({
                    value: "OTHER",
                    text: "Other"
                });

                self.data.source = "OTHER";
            }
        },
        preloadSourceOptions(source, sources) {
            let self = this;
            forEach(sources, function (value, key) {
                if (source.indexOf("IDEXX_DEVICE:") == 0) {
                    self.sourceOptions.push({
                        value: "IDEXX_DEVICE:" + key,
                        text: value
                    });
                } else {
                    self.sourceOptions.push({
                        value: key,
                        text: value
                    });
                }
            });

            orderBy(self.sourceOptions, ["text"]);
        },
        updateFieldsBasedOnSource(module, value, oldValue) {
            let self = this;
            let itemType = this.data.item_type
                ? Number(this.data.item_type)
                : null;

            // self.item is for record/create-update
            // self.data.item is for invoice-item/create-item
            const item =
                self.data && self.data.item ? self.data.item : self.item;

            if (
                (oldValue === "ZNLABS" ||
                    oldValue === "VETEK" ||
                    oldValue === "MICROVET" ||
                    oldValue === "BIONOTE") &&
                oldValue !== value
            ) {
                self.getStaffList(value);

                if (module == "patient-record") {
                    self.getStaffListGivenBy(value);
                }
            }

            if (value && value.indexOf("IDEXX_DEVICE:") == 0) {
                value = "IDEXX_DEVICE";
            }

            if (
                value &&
                value !== "" &&
                itemType === ITEMTYPES.TYPES.TYPE_LAB
            ) {
                self.showSendToLab = true;

                const isAntechV6 = self._isExtensionEnabled(
                    EXTENSIONS.CODE.CODE_ANTECH
                );

                switch (value) {
                    case "ANTECH_WEST":
                    case "ANTECH_EAST":
                    case "ANTECH":
                    case "IDEXX":
                    case "ZNLABS":
                    case "NBIOVET":
                    case "MIDWEST":
                    case "VETEK":
                    case "MICROVET":
                    case "BIONOTE":
                        if (
                            value === "ZNLABS" ||
                            value === "VETEK" ||
                            value === "MICROVET" ||
                            value === "BIONOTE"
                        ) {
                            self.getStaffList(value);
                            if (module == "patient-record") {
                                self.getStaffListGivenBy(value);
                            }
                        }
                        $(".med-record").hide();
                        self.sendToLabLabel = "Send to Lab:";
                        self.data.send_to = 1;

                        if (
                            (self.item?.code == "Y" ||
                                self.data.item?.code == "Y") &&
                            value == "ANTECH" &&
                            isAntechV6
                        ) {
                            self.sendToLabLabel = "Send to Analyzer";
                        }

                        break;
                    case "OTHER":
                    case "Heska":
                    case "Abaxis":
                    case "ANIMAL_REFERENCE_PATHOLOGY":
                        self.showSendToLab = false;
                        $(".send-to-lab").hide();
                        self.data.send_to = 0;
                        break;
                    case "IDEXX_DEVICE":
                        $(".send-to-lab").show();
                        self.sendToLabLabel = "Send to VetLab:";
                        self.data.send_to = 1;

                        self.checkSourceStatus("IDEXX");
                        break;
                    case "HESKA_CLOUD":
                        $(".send-to-lab").show();
                        self.sendToLabLabel = "Send to Analyzer:";
                        self.data.send_to = 1;

                        break;
                    default:
                        // abaxis_api or abaxis_fuse
                        $(".send-to-lab").show();
                        self.sendToLabLabel = "Send to Analyzer:";

                        if (module == "patient-record") {
                            self.data.send_to =
                                self.selectedFirstItem &&
                                self.selectedFirstItem.send_to_lab
                                    ? Number(self.selectedFirstItem.send_to_lab)
                                    : 0;
                        } else {
                            self.data.send_to =
                                self.data.item && self.data.item.send_to_lab
                                    ? Number(self.data.item.send_to_lab)
                                    : 0;
                        }

                        if (
                            item &&
                            item.masterItem &&
                            item.masterItem.source == "ABAXIS_FUSE"
                        ) {
                            self.data.send_to = item.masterItem.isSendToLab
                                ? item.masterItem.isSendToLab
                                : "1";
                        } else if (
                            item.length > 0 &&
                            item[0].masterItem &&
                            item[0].masterItem.source == "ABAXIS_FUSE"
                        ) {
                            // fix for VET-10816, send to analyzer is not default to yes
                            self.data.send_to = item[0].masterItem.isSendToLab
                                ? item[0].masterItem.isSendToLab
                                : "1";
                        }

                        setTimeout(function () {
                            self.checkSourceStatus("ABAXIS_API");
                        }, 1000);

                        break;
                }
            } else {
                self.showSendToLab = false;
                $(".send-to-lab").hide();
                if (module == "patient-record") {
                    self.showTestAntech = false;
                    $(".test-antech").hide();
                }
            }
        },
        checkSourceStatus(extension) {
            Lab.getSourceStatus({
                source: this.data.source,
                extension
            }).then(response => {
                if (
                    response.status &&
                    response.status.toUpperCase() === "OFFLINE"
                ) {
                    let alertMessage = ANALYZER_OFFLINE_MSG;
                    if (response.extension.toUpperCase() == "IDEXX") {
                        alertMessage = VETLAB_STATION_OFFLINE_MSG;
                    }
                    this.pushAlertMessages(alertMessage);
                } else {
                    this.removeAlertMessages(ANALYZER_OFFLINE_MSG);
                    this.removeAlertMessages(VETLAB_STATION_OFFLINE_MSG);
                }
            });
        },
        removeFormError(field) {
            this.formError = this.formError.filter(err => err !== field);
        },
        removeAlertMessages(msg) {
            this.alertMessages = this.alertMessages.filter(err => err !== msg);
        },
        pushAlertMessages(msg) {
            if (!this.alertMessages.includes(msg)) {
                this.alertMessages.push(msg);
            }
        },
        addRefillRule() {
            this.$validator.extend("refill_minimum", {
                validate: (value, args) => {
                    // Only validate for edit action
                    if (this.action && this.action !== "edit") {
                        return true;
                    }

                    // Check if we have refill summary data from multiple sources
                    let refillSummary = null;

                    // Check data.refill_summary first (for create-record-multiple-patient)
                    if (this.data?.refill_summary) {
                        refillSummary = this.data.refill_summary;
                    }
                    // Check params.summary (for create-update.vue)
                    else if (this.params && this.params?.summary) {
                        refillSummary = this.params.summary;
                    }
                    // Check invoiceItem.record.summary (for create-record-multiple-patient)
                    else if (
                        this.invoiceItem &&
                        this.invoiceItem?.record &&
                        this.invoiceItem?.record?.summary
                    ) {
                        refillSummary = this.invoiceItem?.record?.summary;
                        refillSummary.refill = refillSummary.refills;
                        refillSummary.refill_used =
                            refillSummary.refills -
                            refillSummary.remainingRefill;
                    }

                    if (!refillSummary || !refillSummary.refill) {
                        return true;
                    }

                    const refillUsed = refillSummary.refill_used
                        ? parseInt(refillSummary.refill_used)
                        : 0;
                    const refill = parseInt(value) || 0;

                    return refill >= refillUsed;
                },
                params: [],
                message: REFILL_MINIMUM_MSG
            });
        },
        isItemMicrochip(item) {
            let itemName = item.name;
            let displayItemName = item.display_name;

            const isItemMicrochip =
                (itemName && itemName.toLowerCase().includes("microchip")) ||
                (displayItemName &&
                    displayItemName.toLowerCase().includes("microchip"));
            return isItemMicrochip;
        }
    }
};
